/**
 * Deal-verdict engine — P05 (BF hub + deal-verdict layer).
 *
 * Pure and client-safe: no supabase imports, no server code, no I/O. Stats
 * and verdicts are computed ON READ from our own keepa_price_snapshots rows
 * (passed in by the caller) — never from the unpopulated
 * products.lowest_price_ever_* columns and never from Keepa's denormalized
 * lows (those are a candidate prefilter only, upstream in fetch.ts).
 *
 * THE FTC-SAFETY CONTRACT (encoded as behavior below — every user-facing
 * claim this module can emit is derivable from OUR stored snapshots):
 *   1. Render nothing (null) when sampleDays < 14 or currentCents <= 0 —
 *      Real-or-Hidden Rule 3: real values or nothing, never a thin claim.
 *   2. 'all-time-low' ONLY when currentCents <= minCents AND the tracked
 *      history spans >= 180 days. The headline says "we've tracked since
 *      {Month Year}" — the date is that product's own earliest snapshot,
 *      never a site-wide date, and the copy is never "lowest price ever".
 *   3. 'below-average' / 'above-average' use the same 3% band as the
 *      existing PDP price-context badge, against the 90-day average.
 *   4. No "was" prices derived from list/MSRP, no countdown timers, no
 *      scarcity claims. Dollar amounts and dates only ever come from the
 *      stats object computed here.
 */

export interface PriceHistoryStats {
  trackedSinceDate: string
  sampleDays: number
  minCents: number
  minDate: string
  avg30Cents: number | null
  avg90Cents: number | null
  avg365Cents: number | null
  latestSnapshotDate: string
}

export type DealVerdictTier =
  | 'all-time-low'
  | 'below-average'
  | 'typical'
  | 'above-average'

export type DealVerdict = {
  tier: DealVerdictTier
  headline: string
  detail: string
  stats: PriceHistoryStats
  currentCents: number
}

export interface SnapshotRow {
  snapshot_date: string
  new_price_cents: number | null
}

const MS_PER_DAY = 24 * 60 * 60 * 1000
const MIN_SAMPLE_DAYS = 14
const ALL_TIME_LOW_MIN_SPAN_DAYS = 180
const AVG_BAND = 0.03 // mirrors the PDP price-context badge threshold

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** "YYYY-MM-DD" → "Month YYYY" without Date-object timezone drift. */
export function formatMonthYear(isoDate: string): string {
  const [y, m] = isoDate.split('-')
  const monthIdx = Number(m) - 1
  const month = MONTH_NAMES[monthIdx] ?? ''
  return month ? `${month} ${Number(y)}` : isoDate
}

/** UTC-midnight epoch ms for a "YYYY-MM-DD" string. */
function dateMs(isoDate: string): number {
  return Date.parse(`${isoDate.slice(0, 10)}T00:00:00Z`)
}

function averageCents(rows: Array<{ cents: number }>): number | null {
  if (rows.length === 0) return null
  return Math.round(rows.reduce((sum, r) => sum + r.cents, 0) / rows.length)
}

/**
 * Compute history stats from raw snapshot rows. Rows with null or <= 0
 * prices are ignored. Returns null when no valid rows remain. The trailing
 * 30/90/365-day averages are anchored to the LATEST snapshot date (not the
 * wall clock) so the function stays pure and deterministic.
 */
export function computeStats(rows: SnapshotRow[]): PriceHistoryStats | null {
  const valid = rows
    .filter(
      (r): r is { snapshot_date: string; new_price_cents: number } =>
        typeof r.new_price_cents === 'number' &&
        r.new_price_cents > 0 &&
        typeof r.snapshot_date === 'string' &&
        !Number.isNaN(dateMs(r.snapshot_date))
    )
    .map((r) => ({ date: r.snapshot_date.slice(0, 10), cents: r.new_price_cents }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))

  if (valid.length === 0) return null

  const trackedSinceDate = valid[0].date
  const latestSnapshotDate = valid[valid.length - 1].date

  let minCents = valid[0].cents
  let minDate = valid[0].date
  for (const row of valid) {
    if (row.cents < minCents) {
      minCents = row.cents
      minDate = row.date
    }
  }

  const latestMs = dateMs(latestSnapshotDate)
  const inWindow = (days: number) =>
    valid.filter((r) => latestMs - dateMs(r.date) < days * MS_PER_DAY)

  return {
    trackedSinceDate,
    sampleDays: valid.length,
    minCents,
    minDate,
    avg30Cents: averageCents(inWindow(30)),
    avg90Cents: averageCents(inWindow(90)),
    avg365Cents: averageCents(inWindow(365)),
    latestSnapshotDate,
  }
}

function wholeDollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`
}

/**
 * Turn a current price + history stats into a verdict, applying the
 * FTC-safety gates documented at the top of this file. `asOf` (default: now)
 * anchors the >= 180-day span check for all-time-low claims and exists so
 * tests can pin the clock.
 */
export function computeVerdict(
  currentCents: number,
  stats: PriceHistoryStats | null,
  asOf: Date = new Date()
): DealVerdict | null {
  if (!stats) return null
  if (stats.sampleDays < MIN_SAMPLE_DAYS) return null
  if (!(currentCents > 0)) return null

  const spanDays = (asOf.getTime() - dateMs(stats.trackedSinceDate)) / MS_PER_DAY

  if (
    currentCents <= stats.minCents &&
    spanDays >= ALL_TIME_LOW_MIN_SPAN_DAYS
  ) {
    return {
      tier: 'all-time-low',
      headline: `Lowest price we’ve tracked since ${formatMonthYear(stats.trackedSinceDate)}`,
      detail: `Today’s price matches or beats every price we’ve recorded across ${stats.sampleDays.toLocaleString('en-US')} tracked days.`,
      stats,
      currentCents,
    }
  }

  if (stats.avg90Cents !== null && currentCents <= stats.avg90Cents * (1 - AVG_BAND)) {
    return {
      tier: 'below-average',
      headline: `${wholeDollars(stats.avg90Cents - currentCents)} below its 90-day average`,
      detail: `Its 90-day tracked average is ${wholeDollars(stats.avg90Cents)}.`,
      stats,
      currentCents,
    }
  }

  if (stats.avg90Cents !== null && currentCents >= stats.avg90Cents * (1 + AVG_BAND)) {
    return {
      tier: 'above-average',
      // FRIDGE-1113 — the owner struck out the trailing advice: at 58 characters
      // this headline was more than twice its siblings (24-43) and the only one
      // carrying a second clause. The anti-urgency guidance is not lost, it moves
      // to `detail`, where it reads as counsel rather than crowding the verdict.
      headline: 'Above its recent average',
      detail: `Its 90-day tracked average is ${wholeDollars(stats.avg90Cents)} — today’s price is above it. If you can wait, watch this one.`,
      stats,
      currentCents,
    }
  }

  return {
    tier: 'typical',
    headline: 'In line with its typical price',
    detail: `Within 3% of its 90-day tracked average of ${
      stats.avg90Cents !== null ? wholeDollars(stats.avg90Cents) : wholeDollars(currentCents)
    }.`,
    stats,
    currentCents,
  }
}
